import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

/**
 * Optional "Who is this for?" pre-flow. Skippable at every step.
 * Selections are written to sessionStorage so the wizard can use them
 * for template + JacKey suggestions, and never forced on the user.
 */

const RELATIONSHIPS = [
  { id: 'mother', label: 'Mom', emoji: '🌸' },
  { id: 'father', label: 'Dad', emoji: '🌳' },
  { id: 'partner', label: 'Spouse', emoji: '💞' },
  { id: 'daughter', label: 'Daughter', emoji: '🌷' },
  { id: 'son', label: 'Son', emoji: '🌿' },
  { id: 'grandparent', label: 'Grandparent', emoji: '🍃' },
  { id: 'friend', label: 'Friend', emoji: '🤝' },
  { id: 'other', label: 'Other', emoji: '✨' },
];

const REASONS = [
  { id: 'birthday', label: 'Birthday', template: 'birthday' },
  { id: 'anniversary', label: 'Anniversary', template: 'love' },
  { id: 'appreciation', label: 'Appreciation', template: 'gratitude' },
  { id: 'retirement', label: 'Retirement', template: 'gratitude' },
  { id: 'graduation', label: 'Graduation', template: 'celebration' },
  { id: 'encouragement', label: 'Encouragement', template: 'encouragement' },
  { id: 'just-because', label: 'Just Because', template: 'love' },
  { id: 'other', label: 'Other', template: 'gratitude' },
];

export interface WhoIsThisForPrefs {
  relationship?: string;
  reason?: string;
  template?: string;
}

export const WIZARD_PREFS_KEY = 'sin_wizard_prefs_v1';

interface Props {
  onComplete: (prefs: WhoIsThisForPrefs) => void;
  onSkip: () => void;
}

export function WhoIsThisForStep({ onComplete, onSkip }: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [relationship, setRelationship] = useState<string | null>(null);

  const persist = (prefs: WhoIsThisForPrefs) => {
    try { sessionStorage.setItem(WIZARD_PREFS_KEY, JSON.stringify(prefs)); } catch { /* noop */ }
  };

  const handleRelationship = (id: string) => {
    setRelationship(id);
    setTimeout(() => setStep(2), 250);
  };

  const handleReason = (reasonId: string, template: string) => {
    const prefs: WhoIsThisForPrefs = {
      relationship: relationship || undefined,
      reason: reasonId,
      template,
    };
    persist(prefs);
    onComplete(prefs);
  };

  return (
    <div className="container max-w-2xl py-12 sm:py-16">
      <div className="text-center mb-3">
        <p className="text-xs uppercase tracking-widest text-[#00C896] font-medium">
          Optional · 10 seconds
        </p>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.35 }}
        >
          {step === 1 ? (
            <>
              <h1 className="text-3xl sm:text-4xl font-serif text-foreground text-center mb-3">
                Who is this for?
              </h1>
              <p className="text-sm text-muted-foreground text-center mb-10">
                We'll tailor JacKey's suggestions and examples — nothing is locked in.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {RELATIONSHIPS.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => handleRelationship(r.id)}
                    className={`group relative rounded-xl border p-4 flex flex-col items-center gap-2 transition-all ${
                      relationship === r.id
                        ? 'border-[#00C896] bg-[#00C896]/10'
                        : 'border-border bg-card/40 hover:border-[#00C896]/50 hover:bg-card'
                    }`}
                  >
                    <span className="text-2xl">{r.emoji}</span>
                    <span className="text-sm font-medium text-foreground">{r.label}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <h1 className="text-3xl sm:text-4xl font-serif text-foreground text-center mb-3">
                Why are you creating this?
              </h1>
              <p className="text-sm text-muted-foreground text-center mb-10">
                We'll suggest a template that fits — you can change it anytime.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {REASONS.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => handleReason(r.id, r.template)}
                    className="rounded-xl border border-border bg-card/40 p-4 text-sm font-medium text-foreground hover:border-[#00C896]/50 hover:bg-card transition-all"
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-10 flex items-center justify-center gap-6">
        {step === 2 && (
          <button
            onClick={() => setStep(1)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back
          </button>
        )}
        <button
          onClick={onSkip}
          className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors inline-flex items-center gap-1"
        >
          Skip — take me straight in
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

/** Read prefs that the wizard can consume. */
export function readWizardPrefs(): WhoIsThisForPrefs | null {
  try {
    const raw = sessionStorage.getItem(WIZARD_PREFS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as WhoIsThisForPrefs;
  } catch {
    return null;
  }
}

export function clearWizardPrefs() {
  try { sessionStorage.removeItem(WIZARD_PREFS_KEY); } catch { /* noop */ }
}
